import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { RiskBadge, StatusBadge } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../lib/auth";

const RATING_COLORS = {
  low: { bg: "var(--success-soft)", color: "var(--success)" },
  medium: { bg: "var(--warn-soft)", color: "var(--warn)" },
  high: { bg: "var(--danger-soft)", color: "var(--danger)" },
  critical: { bg: "var(--danger-soft)", color: "var(--danger)" },
  unrated: { bg: "var(--bg-2)", color: "var(--muted)" },
};

const STAGE_COLORS = {
  onboarding: { bg: "var(--primary-soft)", color: "var(--primary)" },
  questionnaire: { bg: "var(--primary-soft)", color: "var(--primary)" },
  documents: { bg: "var(--primary-soft)", color: "var(--primary)" },
  risk_scoring: { bg: "var(--warn-soft)", color: "var(--warn)" },
  approval_pending: { bg: "var(--warn-soft)", color: "var(--warn)" },
  approved: { bg: "var(--success-soft)", color: "var(--success)" },
  active: { bg: "var(--success-soft)", color: "var(--success)" },
  review_due: { bg: "var(--warn-soft)", color: "var(--warn)" },
  offboarding_requested: { bg: "var(--danger-soft)", color: "var(--danger)" },
  offboarded: { bg: "var(--bg-2)", color: "var(--muted)" },
};

function Pill({ label, mapObj, testId }) {
  const t = mapObj?.[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {(label || "—").replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const emptyVendor = {
  name: "", category: "saas", services_provided: "", contact_person: "",
  contact_email: "", country: "India", data_processed: [], purpose: "",
  contract_start_date: "", contract_end_date: "",
  dpa_status: "pending", nda_status: "pending", msa_status: "pending",
  iso27001_status: "pending", soc2_status: "pending",
  security_questionnaire_status: "pending",
  risk_rating: "medium", next_review_date: "",
  status: "active", processes_personal_data: true,
  onboarding_stage: "onboarding",
  data_volume: "medium", access_level: "read",
  department: "engineering",
};

export default function Vendors() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "org_admin", "dpo", "vendor_manager"].includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ document_types: [], onboarding_stages: [], categories: [], departments: [] });
  const [dash, setDash] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fRating, setFRating] = useState("");
  const [fStage, setFStage] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fSearch, setFSearch] = useState("");

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyVendor);
  const [editing, setEditing] = useState(null);

  const [detail, setDetail] = useState(null);
  const [docs, setDocs] = useState([]);
  const [detailTab, setDetailTab] = useState("profile");
  const [docForm, setDocForm] = useState({ doc_type: "dpa", title: "", expiry_date: "", notes: "" });
  const [reviewForm, setReviewForm] = useState({ outcome: "continue", notes: "", next_review_date: "" });
  const [qTemplate, setQTemplate] = useState([]);
  const [qData, setQData] = useState({ answers: {}, score: null });

  useEffect(() => {
    if (detail && detailTab === "questionnaire") {
      api.get("/vendors-tprm/questionnaire/template").then(r => setQTemplate(r.data.questions)).catch(() => {});
      api.get(`/vendors-tprm/${detail.id}/questionnaire`).then(r => setQData({ answers: r.data.answers || {}, score: r.data.score, status: r.data.status })).catch(() => {});
    }
     
  }, [detail?.id, detailTab]);

  const saveQuestionnaire = async () => {
    try {
      const { data } = await api.put(`/vendors-tprm/${detail.id}/questionnaire`, { answers: qData.answers });
      setQData(q => ({ ...q, score: data.score, status: data.status }));
      toast.success(`Questionnaire saved — score ${data.score ?? "—"}/100, risk re-scored`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, d, l] = await Promise.all([
        api.get("/vendors-tprm/config"),
        api.get("/vendors-tprm/dashboard"),
        api.get("/vendors", { params: { limit: 1000 } }),
      ]);
      setMeta(m.data);
      setDash(d.data);
      setVendors(l.data.items || l.data || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() => {
    return vendors.filter(v => {
      if (fRating && (v.risk_rating || "unrated") !== fRating) return false;
      if (fStage && (v.onboarding_stage || "active") !== fStage) return false;
      if (fCountry && !(v.country || "").toLowerCase().includes(fCountry.toLowerCase())) return false;
      if (fSearch) {
        const s = fSearch.toLowerCase();
        if (!(v.name || "").toLowerCase().includes(s) &&
            !(v.contact_email || "").toLowerCase().includes(s) &&
            !(v.category || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [vendors, fRating, fStage, fCountry, fSearch]);

  const saveVendor = async () => {
    try {
      const payload = {
        ...form,
        data_processed: typeof form.data_processed === "string"
          ? form.data_processed.split(",").map(s => s.trim()).filter(Boolean)
          : form.data_processed,
      };
      if (editing) {
        await api.put(`/vendors/${editing.id}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor created");
      }
      setModal(false); setEditing(null); setForm(emptyVendor);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openDetail = async (v) => {
    setDetail(v);
    setDetailTab("profile");
    try {
      const { data } = await api.get(`/vendors-tprm/${v.id}/documents`);
      setDocs(data.items || []);
    } catch { setDocs([]); }
  };

  const reloadDetail = async () => {
    if (!detail) return;
    const { data } = await api.get(`/vendors/${detail.id}`);
    setDetail(data);
    const dr = await api.get(`/vendors-tprm/${detail.id}/documents`);
    setDocs(dr.data.items || []);
    loadAll();
  };

  const moveStage = async (stage) => {
    try {
      await api.post(`/vendors-tprm/${detail.id}/stage`, { stage });
      toast.success(`Moved to ${stage.replace(/_/g, " ")}`);
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const approve = async (decision) => {
    const comment = window.prompt(`${decision === "approve" ? "Approval" : "Rejection"} comment:`, "") || "";
    try {
      await api.post(`/vendors-tprm/${detail.id}/approval`, { decision, comment });
      toast.success(`Vendor ${decision}d`);
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const scoreRisk = async () => {
    toast.info("Scoring risk...");
    try {
      const { data } = await api.post(`/vendors-tprm/${detail.id}/score-risk`);
      toast.success(`Risk: ${data.rating} (${data.score})`);
      await reloadDetail();
    } catch { toast.error("Failed"); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("doc_type", docForm.doc_type);
    if (docForm.title) fd.append("title", docForm.title);
    if (docForm.expiry_date) fd.append("expiry_date", docForm.expiry_date);
    if (docForm.notes) fd.append("notes", docForm.notes);
    fd.append("file", file);
    try {
      await api.post(`/vendors-tprm/${detail.id}/documents`, fd);
      toast.success(`Uploaded ${file.name}`);
      setDocForm({ doc_type: "dpa", title: "", expiry_date: "", notes: "" });
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const analyzeDoc = async (docId) => {
    toast.info("Analyzing document...");
    try {
      const { data } = await api.post(`/vendors-tprm/${detail.id}/documents/${docId}/analyze`);
      toast.success(`AI: suggested ${data.suggested_rating}`);
      const dr = await api.get(`/vendors-tprm/${detail.id}/documents`);
      setDocs(dr.data.items || []);
    } catch { toast.error("AI analysis failed"); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/vendors-tprm/${detail.id}/documents/${docId}`);
      toast.success("Deleted");
      await reloadDetail();
    } catch { toast.error("Failed"); }
  };

  const recordReview = async () => {
    try {
      await api.post(`/vendors-tprm/${detail.id}/review`, reviewForm);
      toast.success("Review recorded");
      setReviewForm({ outcome: "continue", notes: "", next_review_date: "" });
      await reloadDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadVendorReport = async () => {
    try {
      const res = await api.get(`/vendors-tprm/${detail.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vendor_report_${(detail.name || "vendor").replace(/[^a-z0-9]/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
  };

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Vendor & Third-Party Risk Management"
        kicker="DPDP Sec. 8(2) TPRM"
        description="Full vendor lifecycle: onboarding → questionnaire → documents → risk scoring → approval → active → periodic review → offboarding, with AI-assisted document analysis."
        testId="tprm-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={loadAll} data-testid="tprm-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            {isAdmin && (
              <Btn onClick={() => { setEditing(null); setForm(emptyVendor); setModal(true); }} data-testid="tprm-new-btn">
                <Icons.Plus className="w-3.5 h-3.5" /> Onboard vendor
              </Btn>
            )}
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "list", label: "Vendors", icon: "Building2" },
          { key: "reviews", label: "Reviews & docs", icon: "FileCheck" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`tprm-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent", color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Total vendors" value={dash.total_vendors} testId="stat-total" />
            <StatCard label="High-risk" value={dash.high_risk_vendors} tone="danger" testId="stat-highrisk" />
            <StatCard label="Processing personal data" value={dash.vendors_processing_pd} testId="stat-pd" />
            <StatCard label="Without DPA" value={dash.vendors_without_dpa} tone="danger" testId="stat-nodpa" />
            <StatCard label="Expired documents" value={dash.vendors_with_expired_docs} tone="danger" testId="stat-expired" />
            <StatCard label="Due for review (30d)" value={dash.vendors_due_review} tone="warn" testId="stat-review" />
            <StatCard label="Countries" value={dash.by_country?.length || 0} testId="stat-countries" />
            <StatCard label="Categories" value={Object.keys(dash.by_category || {}).length} testId="stat-cats" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By risk rating</div>
              <div className="space-y-1.5" data-testid="by-rating">
                {Object.entries(dash.by_rating || {}).map(([k, v]) => {
                  const total = Object.values(dash.by_rating).reduce((a, b) => a + b, 0) || 1;
                  const pct = Math.round(v / total * 100);
                  const c = RATING_COLORS[k]?.color;
                  return (
                    <div key={k}>
                      <div className="flex items-center gap-2 text-[11.5px]"><span className="flex-1 ink">{k}</span><span className="muted">{v}</span></div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full" style={{ width: `${pct}%`, background: c }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By country</div>
              <div className="space-y-1.5" data-testid="by-country-list">
                {(dash.by_country || []).slice(0, 10).map(c => (
                  <div key={c.country} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.MapPin className="w-3 h-3" style={{ color: "var(--primary)" }} />
                    <div className="flex-1 ink">{c.country}</div>
                    <div className="muted">{c.count}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By department</div>
              <div className="space-y-1.5" data-testid="by-dept">
                {Object.entries(dash.by_department || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.Users className="w-3 h-3" style={{ color: "var(--primary)" }} />
                    <div className="flex-1 ink">{k}</div>
                    <div className="muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Expiring soon (30 days)</div>
              {(dash.expiring_soon_docs || []).length === 0 ? (
                <div className="text-[12px] muted">No documents expiring soon. ✅</div>
              ) : (
                <div className="space-y-1.5" data-testid="expiring-soon">
                  {dash.expiring_soon_docs.map(d => (
                    <div key={d.id} className="p-2 rounded-md flex items-center gap-2" style={{ border: "1px solid var(--warn)", background: "var(--warn-soft)" }}>
                      <Icons.Clock className="w-3.5 h-3.5" style={{ color: "var(--warn)" }} />
                      <div className="flex-1 text-[11.5px] ink">{d.title || d.filename}</div>
                      <div className="text-[10.5px] muted">{d.doc_type} · {d.expiry_date}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Latest activity</div>
              {(dash.activity_feed || []).length === 0 ? (
                <div className="text-[12px] muted">No activity.</div>
              ) : (
                <div className="space-y-1.5" data-testid="tprm-activity">
                  {dash.activity_feed.map(ev => (
                    <div key={ev.id} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                      <div className="text-[11.5px] ink"><b>{ev.action.replace(/_/g, " ")}</b> · {ev.vendor_name}</div>
                      <div className="text-[10px] muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "list" && (
        <>
          <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2">
            <TextInput data-testid="filter-search" placeholder="Search name / email / category" value={fSearch} onChange={(e) => setFSearch(e.target.value)} />
            <SelectInput data-testid="filter-rating" value={fRating} onChange={(e) => setFRating(e.target.value)}>
              <option value="">All ratings</option>
              {Object.keys(RATING_COLORS).map(r => <option key={r} value={r}>{r}</option>)}
            </SelectInput>
            <SelectInput data-testid="filter-stage" value={fStage} onChange={(e) => setFStage(e.target.value)}>
              <option value="">All stages</option>
              {meta.onboarding_stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </SelectInput>
            <TextInput data-testid="filter-country" placeholder="Country" value={fCountry} onChange={(e) => setFCountry(e.target.value)} />
          </div>
          {filtered.length === 0 ? (
            <EmptyState title="No vendors match" hint="Adjust filters or onboard a vendor." />
          ) : (
            <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-2)" }}>
                  <tr>
                    {["Vendor", "Category", "Country", "Stage", "DPA", "ISO/SOC2", "Risk", "Next review", ""].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="tprm-vendors-tbody">
                  {filtered.map(v => (
                    <tr key={v.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`tprm-vendor-row-${v.id}`}>
                      <td className="px-4 py-2 ink text-[12px]">
                        <div className="font-medium">{v.name}</div>
                        <div className="text-[10.5px] muted">{v.contact_email || "—"}</div>
                      </td>
                      <td className="px-4 py-2 muted text-[11.5px]">{v.category || v.vendor_type}</td>
                      <td className="px-4 py-2 muted text-[11.5px]">{v.country || "—"}</td>
                      <td className="px-4 py-2"><Pill label={v.onboarding_stage || "active"} mapObj={STAGE_COLORS} /></td>
                      <td className="px-4 py-2"><StatusBadge status={v.dpa_status || "pending"} /></td>
                      <td className="px-4 py-2 text-[11px] muted">
                        <span title="ISO 27001">{v.iso27001_status === "certified" ? "✓" : "—"}</span>
                        <span className="mx-1">/</span>
                        <span title="SOC 2">{v.soc2_status === "certified" ? "✓" : "—"}</span>
                      </td>
                      <td className="px-4 py-2"><RiskBadge level={v.risk_rating} /></td>
                      <td className="px-4 py-2 muted text-[10.5px]">{v.next_review_date?.slice(0, 10) || "—"}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => downloadFile(`/vendors-tprm/${v.id}/report.pdf`, `vendor_${v.id.slice(0, 8)}.pdf`)}
                           className="text-[11px] font-medium hover:underline mr-2" style={{ color: "var(--primary)" }}
                           data-testid={`tprm-download-${v.id}`}>PDF</button>
                        <button onClick={() => openDetail(v)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`tprm-open-${v.id}`}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "reviews" && dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Expired documents</div>
            {(dash.expired_docs || []).length === 0 ? (
              <div className="text-[12px] muted">No expired docs. ✅</div>
            ) : (
              <div className="space-y-1.5" data-testid="expired-docs">
                {dash.expired_docs.map(d => (
                  <div key={d.id} className="p-2 rounded-md" style={{ border: "1px solid var(--danger)", background: "var(--danger-soft)" }}>
                    <div className="text-[11.5px] font-medium ink">{d.title || d.filename}</div>
                    <div className="text-[10.5px] muted">{d.doc_type} · expired {d.expiry_date}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Vendors due for review</div>
            {vendors.filter(v => {
              if (!v.next_review_date) return false;
              const d = new Date(v.next_review_date);
              const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
              return diff <= 30;
            }).map(v => (
              <div key={v.id} className="p-2.5 rounded-md flex items-center gap-2 mb-1.5" style={{ border: "1px solid var(--border)" }}>
                <div className="flex-1">
                  <div className="text-[12px] font-medium ink">{v.name}</div>
                  <div className="text-[10.5px] muted">Due {v.next_review_date?.slice(0, 10)}</div>
                </div>
                <Btn size="sm" variant="secondary" onClick={() => openDetail(v)}>Open</Btn>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NEW / EDIT VENDOR */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit vendor: ${editing.name}` : "Onboard new vendor"}</DialogTitle>
            <DialogDescription>Fill in vendor profile. Risk scoring uses this data + uploaded documents + incidents.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
            <Field label="Vendor name" required><TextInput data-testid="v-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Category">
              <SelectInput data-testid="v-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {meta.categories.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Services provided" className="col-span-2"><TextArea data-testid="v-services" value={form.services_provided} onChange={(e) => setForm({ ...form, services_provided: e.target.value })} rows={2} /></Field>
            <Field label="Contact person"><TextInput data-testid="v-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
            <Field label="Contact email"><TextInput type="email" data-testid="v-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
            <Field label="Country"><TextInput data-testid="v-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
            <Field label="Owning department">
              <SelectInput data-testid="v-dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                {meta.departments.map(d => <option key={d} value={d}>{d}</option>)}
              </SelectInput>
            </Field>
            <Field label="Data processed (comma-sep)" className="col-span-2"><TextInput data-testid="v-data" value={Array.isArray(form.data_processed) ? form.data_processed.join(", ") : form.data_processed} onChange={(e) => setForm({ ...form, data_processed: e.target.value })} /></Field>
            <Field label="Purpose of processing" className="col-span-2"><TextArea data-testid="v-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} rows={2} /></Field>
            <Field label="Data volume">
              <SelectInput data-testid="v-vol" value={form.data_volume} onChange={(e) => setForm({ ...form, data_volume: e.target.value })}>
                {["low", "medium", "high", "very_high"].map(x => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Access level">
              <SelectInput data-testid="v-access" value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })}>
                {["read", "write", "admin", "root"].map(x => <option key={x} value={x}>{x}</option>)}
              </SelectInput>
            </Field>
            <Field label="Contract start"><TextInput type="date" data-testid="v-cstart" value={form.contract_start_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, contract_start_date: e.target.value })} /></Field>
            <Field label="Contract end"><TextInput type="date" data-testid="v-cend" value={form.contract_end_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} /></Field>
            <Field label="DPA status">
              <SelectInput data-testid="v-dpa" value={form.dpa_status} onChange={(e) => setForm({ ...form, dpa_status: e.target.value })}>
                {["pending", "in_review", "signed", "expired"].map(x => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="NDA status">
              <SelectInput data-testid="v-nda" value={form.nda_status} onChange={(e) => setForm({ ...form, nda_status: e.target.value })}>
                {["pending", "in_review", "signed", "expired"].map(x => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="ISO 27001 status">
              <SelectInput data-testid="v-iso" value={form.iso27001_status} onChange={(e) => setForm({ ...form, iso27001_status: e.target.value })}>
                {["pending", "in_progress", "certified", "expired", "not_applicable"].map(x => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="SOC 2 status">
              <SelectInput data-testid="v-soc" value={form.soc2_status} onChange={(e) => setForm({ ...form, soc2_status: e.target.value })}>
                {["pending", "in_progress", "certified", "expired", "not_applicable"].map(x => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Next review date"><TextInput type="date" data-testid="v-review" value={form.next_review_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, next_review_date: e.target.value })} /></Field>
            <Field label="Onboarding stage">
              <SelectInput data-testid="v-stage" value={form.onboarding_stage} onChange={(e) => setForm({ ...form, onboarding_stage: e.target.value })}>
                {meta.onboarding_stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn onClick={saveVendor} disabled={!form.name} data-testid="v-save">{editing ? "Save" : "Onboard"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL WORKBENCH */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-5xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span>{detail.name}</span>
                  <Pill label={detail.onboarding_stage || "active"} mapObj={STAGE_COLORS} testId="detail-stage" />
                  <Pill label={detail.risk_rating || "unrated"} mapObj={RATING_COLORS} testId="detail-rating" />
                  {detail.risk_score !== undefined && <span className="text-[11px] mono muted">score {detail.risk_score}/100</span>}
                </DialogTitle>
                <DialogDescription>{detail.category?.replace(/_/g, " ")} · {detail.country} · {detail.contact_email || "—"}</DialogDescription>
              </DialogHeader>

              <div className="mb-3 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
                {[
                  { key: "profile", label: "Profile", icon: "User" },
                  { key: "documents", label: "Documents", icon: "FileText" },
                  { key: "questionnaire", label: "Questionnaire", icon: "ListChecks" },
                  { key: "risk", label: "Risk & incidents", icon: "AlertTriangle" },
                  { key: "review", label: "Reviews", icon: "Calendar" },
                  { key: "activity", label: "Activity", icon: "Activity" },
                ].map(t => {
                  const Icon = Icons[t.icon] || Icons.Circle;
                  return (
                    <button key={t.key} data-testid={`detail-subtab-${t.key}`} onClick={() => setDetailTab(t.key)}
                      className="px-2.5 py-1 text-[11px] font-medium rounded-md flex items-center gap-1"
                      style={{ background: detailTab === t.key ? "var(--primary-soft)" : "transparent", color: detailTab === t.key ? "var(--primary)" : "var(--muted)" }}>
                      <Icon className="w-3 h-3" /> {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="max-h-[62vh] overflow-y-auto pr-1">
                {detailTab === "profile" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                      {[
                        ["Contact", detail.contact_person],
                        ["Email", detail.contact_email],
                        ["Country", detail.country],
                        ["Department", detail.department],
                        ["Category", detail.category],
                        ["Contract start", detail.contract_start_date?.slice(0, 10)],
                        ["Contract end", detail.contract_end_date?.slice(0, 10)],
                        ["Next review", detail.next_review_date?.slice(0, 10)],
                        ["DPA", detail.dpa_status],
                        ["NDA", detail.nda_status],
                        ["ISO 27001", detail.iso27001_status],
                        ["SOC 2", detail.soc2_status],
                        ["Data volume", detail.data_volume],
                        ["Access level", detail.access_level],
                      ].map(([k, v]) => (
                        <div key={k} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                          <div className="text-[10px] font-semibold uppercase tracking-wider muted">{k}</div>
                          <div className="text-[12px] ink mt-0.5 font-medium">{v || "—"}</div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider muted mb-1">Data processed</div>
                      <div className="text-[12px] ink">{(detail.data_processed || []).join(", ") || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider muted mb-1">Purpose</div>
                      <div className="text-[12px] ink">{detail.purpose || "—"}</div>
                    </div>
                    {isAdmin && (
                      <div className="flex flex-wrap gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
                        <Btn size="sm" variant="secondary" onClick={() => { setEditing(detail); setForm({ ...emptyVendor, ...detail }); setModal(true); }} data-testid="detail-edit"><Icons.Pencil className="w-3 h-3" /> Edit</Btn>
                        {meta.onboarding_stages.map(s => (
                          <Btn key={s} size="sm" variant={detail.onboarding_stage === s ? "primary" : "secondary"} onClick={() => moveStage(s)} data-testid={`stage-${s}`}>
                            {s.replace(/_/g, " ")}
                          </Btn>
                        ))}
                      </div>
                    )}
                    {isAdmin && detail.onboarding_stage === "approval_pending" && (
                      <div className="flex gap-2">
                        <Btn size="sm" onClick={() => approve("approve")} data-testid="detail-approve">Approve</Btn>
                        <Btn size="sm" variant="secondary" onClick={() => approve("reject")} data-testid="detail-reject" style={{ color: "var(--danger)" }}>Reject</Btn>
                      </div>
                    )}
                  </div>
                )}

                {detailTab === "documents" && (
                  <div className="space-y-4">
                    {isAdmin && (
                      <div className="p-3 rounded-md" style={{ border: "1px solid var(--primary)", background: "var(--primary-soft)" }}>
                        <div className="text-[11.5px] font-semibold mb-2" style={{ color: "var(--primary)" }}>Upload document</div>
                        <div className="grid grid-cols-2 gap-2">
                          <Field label="Document type">
                            <SelectInput data-testid="doc-type" value={docForm.doc_type} onChange={(e) => setDocForm({ ...docForm, doc_type: e.target.value })}>
                              {meta.document_types.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                            </SelectInput>
                          </Field>
                          <Field label="Expiry date"><TextInput type="date" data-testid="doc-expiry" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} /></Field>
                          <Field label="Title / label" className="col-span-2"><TextInput data-testid="doc-title" value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} /></Field>
                          <Field label="Notes" className="col-span-2"><TextArea data-testid="doc-notes" value={docForm.notes} onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })} rows={2} /></Field>
                          <div className="col-span-2">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-[11.5px] font-medium bg-white" style={{ border: "1px solid var(--primary)", color: "var(--primary)" }}>
                              <Icons.Upload className="w-3.5 h-3.5" /> Choose file & upload
                              <input type="file" data-testid="doc-file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0])} />
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2" data-testid="docs-list">
                      {docs.length === 0 ? <div className="text-[12px] muted">No documents uploaded.</div> : docs.map(d => (
                        <div key={d.id} className="p-3 rounded-md" style={{ border: d.expired ? "1px solid var(--danger)" : d.expiring_soon ? "1px solid var(--warn)" : "1px solid var(--border)" }} data-testid={`doc-${d.id}`}>
                          <div className="flex items-start gap-2">
                            <Icons.FileText className="w-4 h-4 mt-0.5" style={{ color: "var(--primary)" }} />
                            <div className="flex-1 min-w-0">
                              <div className="text-[12.5px] ink font-medium truncate">{d.title || d.filename}</div>
                              <div className="text-[10.5px] muted mt-0.5 flex flex-wrap gap-x-2">
                                <span className="mono">{d.doc_type}</span>
                                <span>·</span>
                                <span>{Math.round((d.size || 0) / 1024)} KB</span>
                                {d.expiry_date && (
                                  <>
                                    <span>·</span>
                                    <span style={{ color: d.expired ? "var(--danger)" : d.expiring_soon ? "var(--warn)" : "var(--muted)" }}>
                                      {d.expired ? "expired " : d.expiring_soon ? "expires " : "expires "}
                                      {d.expiry_date}
                                    </span>
                                  </>
                                )}
                              </div>
                              {d.ai_analysis && (
                                <div className="mt-2 p-2 rounded" style={{ background: "var(--bg-2)" }}>
                                  <div className="text-[10.5px] muted mb-1">AI analysis (suggested rating: <b style={{ color: "var(--primary)" }}>{d.ai_analysis.suggested_rating}</b>)</div>
                                  <div className="text-[11px] ink">{d.ai_analysis.summary}</div>
                                  {(d.ai_analysis.missing_clauses || []).length > 0 && (
                                    <div className="mt-1 text-[10.5px]">
                                      <b>Missing clauses:</b>{" "}
                                      {d.ai_analysis.missing_clauses.map((c, i) => (
                                        <span key={i} className="inline-block px-1.5 py-0.5 rounded mr-1 mono" style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>{c}</span>
                                      ))}
                                    </div>
                                  )}
                                  {(d.ai_analysis.dpdp_gaps || []).length > 0 && (
                                    <div className="mt-1 text-[10.5px]">
                                      <b>DPDP gaps:</b> {d.ai_analysis.dpdp_gaps.join(", ")}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 items-end shrink-0">
                              <button onClick={() => downloadFile(`/vendors-tprm/${detail.id}/documents/${d.id}/download`, d.filename)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`doc-dl-${d.id}`}>Download</button>
                              <button onClick={() => analyzeDoc(d.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`doc-analyze-${d.id}`}>Analyze with AI</button>
                              {isAdmin && <button onClick={() => deleteDoc(d.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`doc-del-${d.id}`}>Delete</button>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailTab === "risk" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
                      <div>
                        <div className="text-[10.5px] font-semibold uppercase tracking-wider muted">Current risk</div>
                        <div className="text-2xl font-semibold mt-1" style={{ color: RATING_COLORS[detail.risk_rating]?.color || "var(--muted)" }}>
                          {detail.risk_rating || "unrated"} <span className="text-[13px] muted">/ {detail.risk_score || 0} of 100</span>
                        </div>
                        <div className="text-[10.5px] muted mt-0.5">Last scored: {detail.risk_scored_at?.slice(0, 10) || "never"}</div>
                      </div>
                      <Btn onClick={scoreRisk} data-testid="risk-rescore"><Icons.Calculator className="w-3.5 h-3.5" /> Re-score</Btn>
                    </div>
                    {(detail.risk_factors || []).length > 0 && (
                      <div>
                        <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Risk factors breakdown</div>
                        <div className="space-y-1.5" data-testid="risk-factors">
                          {detail.risk_factors.map((f, i) => (
                            <div key={i} className="p-2 rounded-md flex items-center gap-2" style={{ border: "1px solid var(--border)" }}>
                              <div className="text-[11px] font-bold w-10 text-right"
                                style={{ color: f.add > 0 ? "var(--danger)" : "var(--success)" }}>
                                {f.add > 0 ? `+${f.add}` : f.add}
                              </div>
                              <div className="flex-1">
                                <div className="text-[12px] ink">{f.factor.replace(/_/g, " ")}</div>
                                {f.detail && <div className="text-[10.5px] muted">{f.detail}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {detailTab === "review" && (
                  <div className="space-y-4">
                    <div className="p-3 rounded-md" style={{ border: "1px solid var(--primary)", background: "var(--primary-soft)" }}>
                      <div className="text-[11.5px] font-semibold mb-2" style={{ color: "var(--primary)" }}>Record periodic review</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Outcome">
                          <SelectInput data-testid="review-outcome" value={reviewForm.outcome} onChange={(e) => setReviewForm({ ...reviewForm, outcome: e.target.value })}>
                            {["continue", "remediate", "terminate"].map(o => <option key={o} value={o}>{o}</option>)}
                          </SelectInput>
                        </Field>
                        <Field label="Next review (optional)"><TextInput type="date" data-testid="review-next" value={reviewForm.next_review_date} onChange={(e) => setReviewForm({ ...reviewForm, next_review_date: e.target.value })} /></Field>
                        <Field label="Notes" className="col-span-2"><TextArea data-testid="review-notes" value={reviewForm.notes} onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })} rows={2} /></Field>
                      </div>
                      <Btn size="sm" className="mt-2" onClick={recordReview} data-testid="review-save"><Icons.Save className="w-3 h-3" /> Record review</Btn>
                    </div>
                    <div>
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Review history ({(detail.review_history || []).length})</div>
                      <div className="space-y-1.5" data-testid="review-history">
                        {(detail.review_history || []).slice().reverse().map(r => (
                          <div key={r.id} className="p-2 rounded-md" style={{ border: "1px solid var(--border)" }}>
                            <div className="text-[12px] ink"><b>{r.outcome}</b> · {r.reviewer}</div>
                            <div className="text-[10.5px] muted">{r.reviewed_at?.slice(0, 19).replace("T", " ")}</div>
                            {r.notes && <div className="text-[11px] muted mt-1">{r.notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {detailTab === "questionnaire" && (
                  <div className="space-y-2" data-testid="questionnaire-tab">
                    <div className="flex items-center justify-between p-3 rounded-md" style={{ background: "var(--primary-soft)" }}>
                      <div>
                        <div className="text-[12px] font-semibold" style={{ color: "var(--primary)" }}>DPDP Security Questionnaire</div>
                        <div className="text-[10.5px] muted">15 weighted questions · the score automatically feeds the vendor risk engine</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold ink" data-testid="questionnaire-score">{qData.score ?? "—"}<span className="text-[11px] muted">/100</span></div>
                        {qData.status && <div className="text-[9.5px] muted uppercase tracking-wide">{qData.status.replace(/_/g, " ")}</div>}
                      </div>
                    </div>
                    {qTemplate.map((q, i) => (
                      <div key={q.id} className="p-2.5 rounded-md" style={{ border: "1px solid var(--border)" }} data-testid={`q-${q.id}`}>
                        <div className="text-[11.5px] ink mb-1.5"><b>{i + 1}.</b> {q.q}{" "}
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase" style={{ background: "var(--bg-2)", color: "var(--muted)" }}>{q.cat} · w{q.weight}</span>
                        </div>
                        <div className="flex gap-1">
                          {["yes", "partial", "no", "na"].map(a => (
                            <button key={a} data-testid={`q-${q.id}-${a}`}
                              onClick={() => setQData(p => ({ ...p, answers: { ...p.answers, [q.id]: a } }))}
                              className="px-2.5 py-1 text-[10.5px] font-semibold rounded-md uppercase"
                              style={{
                                border: qData.answers[q.id] === a ? "1.5px solid var(--primary)" : "1px solid var(--border)",
                                background: qData.answers[q.id] === a ? "var(--primary-soft)" : "transparent",
                                color: qData.answers[q.id] === a ? "var(--primary)" : "var(--muted)",
                              }}>{a}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Btn onClick={saveQuestionnaire} data-testid="questionnaire-save"><Icons.Save className="w-3.5 h-3.5" /> Save & score</Btn>
                  </div>
                )}

                {detailTab === "activity" && (
                  <div className="space-y-1.5" data-testid="detail-activity">
                    {(detail.activity_log || []).length === 0 ? (
                      <div className="text-[12px] muted">No activity yet.</div>
                    ) : (
                      detail.activity_log.slice().reverse().map(ev => (
                        <div key={ev.id} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                          <div className="text-[11.5px] ink"><b>{ev.action.replace(/_/g, " ")}</b></div>
                          <div className="text-[10px] muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
                          {ev.details && Object.keys(ev.details).length > 0 && (
                            <div className="text-[10.5px] muted mt-0.5">{JSON.stringify(ev.details)}</div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Btn variant="secondary" onClick={downloadVendorReport} data-testid="detail-download-report">
                  <Icons.Download className="w-3.5 h-3.5" /> Download report
                </Btn>
                <Btn variant="secondary" onClick={() => setDetail(null)}>Close</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
